import { ReactNode } from 'react';
import IconCircle from './IconCircle';

type Size = 'compact' | 'md' | 'lg';

interface Props {
  icon: ReactNode;
  title: string;
  body?: ReactNode;
  /** Optional call to action under the body (the artboards' "Clear filters" `.btn`). */
  action?: ReactNode;
  className?: string;
  /**
   * `md` (default) = Exam `.empty` (48px 16px, 44px icon, 17px title); `lg` = Students `.empty`
   * (56px 16px, 56px icon, 18px title); `compact` = an empty block inside a section.
   */
  size?: Size;
  /** Shorthand for `size="compact"`. */
  compact?: boolean;
  /** Render the title as plain text instead of a heading, when the page already owns the heading. */
  plainTitle?: boolean;
}

const WRAP: Record<Size, string> = { compact: 'px-4 py-4', md: 'px-4 py-12', lg: 'px-4 py-14' };
const ICON: Record<Size, 'md' | 'xl'> = { compact: 'md', md: 'md', lg: 'xl' };
const TITLE: Record<Size, string> = { compact: 'mt-2 text-sm', md: 'mt-3.5 text-[17px]', lg: 'mt-3.5 text-[18px]' };
const BODY: Record<Size, string> = { compact: 'text-xs', md: 'mt-1.5 text-sm', lg: 'mt-2 text-sm' };
const ACTION: Record<Size, string> = { compact: 'mt-2', md: 'mt-[18px]', lg: 'mt-4' };

export default function EmptyState({ icon, title, body, action, className = '', size, compact = false, plainTitle = false }: Props) {
  const sz: Size = size ?? (compact ? 'compact' : 'md');
  const titleClass = `${TITLE[sz]} font-display font-bold text-ink`;
  return (
    <div className={`flex flex-col items-center justify-center text-center ${WRAP[sz]} ${className}`}>
      <IconCircle icon={icon} tone="neutral" size={ICON[sz]} />
      {plainTitle ? <p className={titleClass}>{title}</p> : <h4 className={titleClass}>{title}</h4>}
      {body && <p className={`${BODY[sz]} max-w-[360px] text-muted`}>{body}</p>}
      {action && <div className={`${ACTION[sz]} flex flex-wrap justify-center gap-2.5`}>{action}</div>}
    </div>
  );
}
